import type { CSSProperties } from '@vue/runtime-dom'
import type { LiteralUnion } from 'type-fest'
import type { ExtractPropTypes, VueConstructor } from 'vue'

import { useModal } from '@/store/modal'
import type { VueComponent, VueComponentInstance } from '@/utils/vue'

declare module 'vue/types/vue' {
  interface Vue {
    $dialog: Dialog;
  }
}
interface Button {
  props?: any;
  label: string;
}
type Buttons = Record<string, Button>

export interface OpenProps<T extends VueComponent> {
  dialog?: any;
  maxWidth?: CSSProperties['maxWidth'] | number;
  component?: Omit<ExtractPropTypes<VueComponentInstance<T>['$props']['props']>, 'resolve' | 'reject'>,
  scrollable?: boolean;
  persistant?: boolean;
}

const defaultProps: OpenProps<any> = {
  maxWidth: 500,
  scrollable: false,
  persistant: true,
}

// <U extends VueConstructor<Vue>
function open<T extends VueComponent, U = unknown>(component: T, props: OpenProps<typeof component> = defaultProps): Promise<U | 'close'> {
  const modal = useModal()
  return new Promise((resolve, reject) => {
    modal.SET_MODAL_CONTENT(component)
    modal.SET_MODAL_PROPS({ ...defaultProps, ...props })
    modal.SET_MODAL_STATUS(true)

    modal.SET_MODAL_RESOLVE(resolve)
    modal.SET_MODAL_REJECT(reject)
  })
}

class Dialog {
  // eslint-disable-next-line unicorn/no-object-as-default-parameter
  open = open

  alert(title: string, message: string, buttonLabel = 'OK', props: any = {}) {
    props.buttonLabel = buttonLabel
    props.title = title
    props.message = message

    const modal = useModal()

    return new Promise((resolve, reject) => {
      modal.SET_ALERT_PROPS(props)
      modal.SET_ALERT_STATUS(true)

      modal.SET_ALERT_RESOLVE(resolve)
      modal.SET_ALERT_REJECT(reject)
    })
  }

  confirm <T extends Buttons = Buttons>(title: string, message: string, buttons: T, props: any = {}): Promise<keyof T> {
    props.title = title
    props.message = message
    props.buttons = buttons

    const modal = useModal()

    return new Promise((resolve, reject) => {
      modal.SET_CONFIRM_PROPS(props)
      modal.SET_CONFIRM_STATUS(true)

      modal.SET_CONFIRM_RESOLVE(resolve)
      modal.SET_CONFIRM_REJECT(reject)
    })
  }

  prompt(
    title: string,
    message: string,
    acceptLabel = 'OK',
    cancelLabel = 'Cancel',
    props: any = {},
  ): Promise<LiteralUnion<'cancelled', string>> {
    props.acceptLabel = acceptLabel
    props.cancelLabel = cancelLabel
    props.title = title
    props.message = message

    const modal = useModal()

    return new Promise((resolve, reject) => {
      modal.SET_PROMPT_PROPS(props)
      modal.SET_PROMPT_STATUS(true)

      modal.SET_PROMPT_RESOLVE(resolve)
      modal.SET_PROMPT_REJECT(reject)
    })
  }
}

const $dialog = new Dialog()

export default {
  install: (Vue: VueConstructor) => {
    Vue.prototype.$dialog = $dialog
  },
}

export const useDialog = () => $dialog
